/**
 * Firestore-backed rating storage.
 * Mirrors plugin/lectura:
 * - users/{uid}/ratings/{presentationId}
 * - presentations/{presentationId}/ratings/{uid}
 */

import type { LecturaUser, RatingStorage } from '../core/types';
import {
  ensureFirestoreDb,
  getUserRatingDoc,
  getPresentationRatingDoc,
  type FirestoreServiceOptions
} from './firestore-service';

interface CreateFirestoreRatingStorageOptions extends FirestoreServiceOptions {
  getCurrentUser: () => LecturaUser | null;
}

export function createFirestoreRatingStorage(opts: CreateFirestoreRatingStorageOptions): RatingStorage {
  return {
    recordRating(presId: string, stars: number): void {
      const user = opts.getCurrentUser();
      if (!user?.uid || user.isAnonymous) return;
      const value = Math.max(1, Math.min(5, Number(stars) || 0));
      if (!value) return;
      ensureFirestoreDb(opts)
        .then((db) => {
          const userRef = getUserRatingDoc(db, user.uid, presId) as unknown as {
            set(data: Record<string, unknown>, opts?: { merge?: boolean }): Promise<void>;
          };
          const presRef = getPresentationRatingDoc(db, user.uid, presId) as unknown as {
            set(data: Record<string, unknown>, opts?: { merge?: boolean }): Promise<void>;
          };
          const payload = { stars: value, ratedAt: Date.now() };
          const presPayload = {
            stars: value,
            ratedAt: Date.now(),
            displayName: user.displayName || null,
            email: user.email || null
          };
          userRef.set(payload, { merge: true }).catch(() => {});
          presRef.set(presPayload, { merge: true }).catch(() => {});
        })
        .catch(() => {});
    },

    getMyRating(presId: string): Promise<number | null> {
      const user = opts.getCurrentUser();
      if (!user?.uid || user.isAnonymous) return Promise.resolve(null);
      return ensureFirestoreDb(opts)
        .then((db) => {
          const ref = getUserRatingDoc(db, user.uid, presId) as unknown as {
            get(): Promise<{ exists: boolean; data(): Record<string, unknown> }>;
          };
          return ref.get().then((snap) => {
            if (!snap.exists) return null;
            const stars = Number((snap.data() || {}).stars);
            if (!Number.isFinite(stars) || stars < 1 || stars > 5) return null;
            return stars;
          });
        })
        .catch(() => null);
    }
  };
}
